import React from 'react';
import { useNavigate } from 'react-router-dom';
import { localizedValue } from '../localization/presentationLanguage';
const policyText = (es, en) => localizedValue({ es, en });

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  // Importante: en lugar de navigate(-1), volvemos a una ruta estable del build actual
  // para evitar 404 por bundles viejos en el historial del navegador.
  const goBack = () => {
    navigate('/home', { replace: true }); // volver al home de la app
  };

  return (
    <div id="privacyRoot" style={styles.wrap}>
      <style>{`
        @keyframes wheelSpin {
          from { transform: translate(-50%, -50%) rotate(0deg); }
          to   { transform: translate(-50%, -50%) rotate(360deg); }
        }
        @keyframes twinkle {
          0%,100% { opacity: .55; transform: translateY(0); }
          50%     { opacity: .9;  transform: translateY(-2px); }
        }
        @media (prefers-reduced-motion: reduce) {
          .pp-wheel { animation: none !important; }
        }
        /* iOS smooth scroll + safe areas */
        #privacyRoot {
          -webkit-overflow-scrolling: touch;
          overscroll-behavior-y: contain;
        }
      `}</style>

      {/* Fondo base violeta/dorado (idéntico estilo Welcome/Login) */}
      <div aria-hidden="true" style={styles.bgBase} />
      {/* Glow suave + blur leve */}
      <div aria-hidden="true" style={styles.bgGlow} />
      {/* Estrellas sutiles */}
      <div aria-hidden="true" style={styles.bgStars} />
      {/* Ruleta centrada girando */}
      <div aria-hidden="true" style={styles.bgWheel} className="pp-wheel" />
      {/* Vignette para profundidad */}
      <div aria-hidden="true" style={styles.vignette} />

      {/* Header glass fijo */}
      <div style={styles.header}>
        <button onClick={goBack} style={styles.backButton} aria-label={policyText('Volver a registro', 'Back to registration')}>
          {policyText('← Volver', '← Back')}
        </button>
        <div style={styles.headerTitleWrap}>
          <div style={styles.headerTitle}>{policyText('Política de Privacidad', 'Privacy Policy')}</div>
          <div style={styles.headerSub}>{policyText('Tus datos, tu control • Transparencia Zodiacus', 'Your data, your control • Zodiacus transparency')}</div>
        </div>
      </div>

      {/* Contenido */}
      <main style={styles.main}>
        <article style={styles.card}>
          <div style={styles.badgeRow}>
            <span style={styles.badge}>{policyText('🔒 Privacidad', '🔒 Privacy')}</span>
            <span style={styles.badgeAlt}>{policyText('🛡️ Seguridad', '🛡️ Security')}</span>
            <span style={styles.badgeAlt2}>{policyText('✨ Transparencia', '✨ Transparency')}</span>
          </div>

          <p style={styles.lead}>
            {policyText('En Zodiacus nos comprometemos a proteger tu privacidad. Esta política explica qué datos recopilamos, cómo los utilizamos y cuáles son tus derechos.', 'Zodiacus is committed to protecting your privacy. This policy explains what data we collect, how we use it, and your rights.')}
          </p>

          <section style={styles.section}>
            <h2 style={styles.h2}><span style={styles.h2Dot}>1</span> {policyText('Datos que recopilamos', 'Data we collect')}</h2>
            <ul style={styles.list}>
              <li>{policyText('Información de registro (nombre, fecha de nacimiento, género).', 'Registration information (name, date of birth, and gender).')}</li>
              <li>{policyText('Imagen del DNI (opcional/si se solicita) para verificación de identidad.', 'Identity-document image (optional/when requested) for identity verification.')}</li>
              <li>{policyText('Datos de uso (interacciones dentro de la app) para mejorar la experiencia.', 'Usage data (interactions within the app) to improve the experience.')}</li>
            </ul>
          </section>

          <div style={styles.divider} />

          <section style={styles.section}>
            <h2 style={styles.h2}><span style={styles.h2Dot}>2</span> {policyText('Finalidad del tratamiento', 'How we use data')}</h2>
            <p style={styles.p}>
              {policyText('Usamos tus datos para crear tu cuenta, verificar tu identidad cuando corresponda, mejorar la seguridad y ofrecerte funcionalidades de compatibilidad zodiacal.', 'We use your data to create your account, verify your identity when appropriate, improve security, and provide zodiac-compatibility features.')}
            </p>
          </section>

          <div style={styles.divider} />

          <section style={styles.section}>
            <h2 style={styles.h2}><span style={styles.h2Dot}>3</span> {policyText('Almacenamiento y seguridad', 'Storage and security')}</h2>
            <p style={styles.p}>
              {policyText('Tu información se almacena de forma segura. No compartimos tus datos con terceros sin tu consentimiento, salvo obligación legal o requerimiento de autoridad competente.', 'Your information is stored securely. We do not share your data with third parties without your consent, except when required by law or a competent authority.')}
            </p>
            <div style={styles.callout}>
              <div style={styles.calloutIcon} aria-hidden="true">🧠</div>
              <div>
                <div style={styles.calloutTitle}>{policyText('Principio de minimización', 'Data minimization')}</div>
                <div style={styles.calloutText}>{policyText('Recopilamos lo necesario para que Zodiacus funcione y mejore, sin excesos.', 'We collect only what Zodiacus needs to operate and improve.')}</div>
              </div>
            </div>
          </section>

          <div style={styles.divider} />

          <section style={styles.section}>
            <h2 style={styles.h2}><span style={styles.h2Dot}>4</span> {policyText('Tus derechos', 'Your rights')}</h2>
            <p style={styles.p}>
              {policyText('Podés solicitar acceso, rectificación o eliminación de tus datos. Si querés ejercer estos derechos, contactanos a través de los canales indicados en la app.', 'You may request access to, correction of, or deletion of your data. To exercise these rights, contact us through the channels provided in the app.')}
            </p>
          </section>

          <div style={styles.divider} />

          <section style={styles.section}>
            <h2 style={styles.h2}><span style={styles.h2Dot}>5</span> {policyText('Contacto', 'Contact')}</h2>
            <p style={styles.p}>
              {policyText('Si tenés dudas sobre esta política, escribinos desde el área de soporte en Zodiacus.', 'If you have questions about this policy, contact us through the Zodiacus support area.')}
            </p>
          </section>

          <div style={styles.footerNote}>
            <span aria-hidden="true">🌙</span>
            <span>{policyText('Última actualización:', 'Last updated:')} <b>2025</b> • {policyText('Esta política puede actualizarse para reflejar mejoras y cambios legales.', 'This policy may be updated to reflect improvements and legal changes.')}</span>
          </div>
        </article>
      </main>
    </div>
  );
}

const styles = {
  wrap: {
    minHeight: '100dvh',
    position: 'relative',
    isolation: 'isolate',
    overflowX: 'hidden',
    overflowY: 'auto',
    fontFamily: '"Poppins", system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
    color: '#EDE7FF',
  },

  /* Background layers */
  bgBase: {
    position: 'fixed',
    inset: 0,
    zIndex: -4,
    pointerEvents: 'none',
    background:
      'radial-gradient(120% 100% at 50% 30%, rgba(255,215,0,0.12), rgba(75,0,130,0.18)), linear-gradient(180deg, #3b0a70 0%, #4a0f90 100%)',
    backgroundColor: '#3b0a70',
  },
  bgGlow: {
    position: 'fixed',
    inset: '-3vmax',
    zIndex: -3,
    pointerEvents: 'none',
    background:
      'radial-gradient(120% 140% at 50% 55%, rgba(75,0,130,0.40) 0%, rgba(75,0,130,0.30) 55%, rgba(48,16,94,0.18) 100%), radial-gradient(140% 160% at 18% 18%, rgba(218,165,32,0.10) 0%, rgba(218,165,32,0) 60%)',
    filter: 'blur(18px) saturate(1.06) brightness(1.10)',
    transform: 'scale(1.02)',
    opacity: 0.32,
    willChange: 'transform, filter, opacity',
  },
  bgStars: {
    position: 'fixed',
    inset: 0,
    zIndex: -2,
    pointerEvents: 'none',
    background:
      'radial-gradient(1px 1px at 20% 30%, rgba(255,255,255,0.9), transparent 60%), radial-gradient(1px 1px at 80% 70%, rgba(255,255,255,0.7), transparent 60%), radial-gradient(1px 1px at 50% 10%, rgba(255,255,255,0.8), transparent 60%), radial-gradient(1px 1px at 10% 90%, rgba(255,255,255,0.6), transparent 60%)',
    backgroundRepeat: 'repeat',
    backgroundSize: '300px 300px',
    animation: 'twinkle 12s linear infinite',
    opacity: 0.55,
  },
  bgWheel: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    width: 'min(96vmin, 980px)',
    height: 'min(96vmin, 980px)',
    transform: 'translate(-50%, -50%)',
    borderRadius: '50%',
    pointerEvents: 'none',
    zIndex: -1,
    backgroundImage: 'url("/logo/Zodiacus.jpeg")',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center center',
    backgroundSize: 'contain',
    filter: 'brightness(1.08) contrast(1.12) saturate(1.08)',
    boxShadow: '0 0 80px rgba(0,0,0,0.28), inset 0 0 18px rgba(0,0,0,0.22)',
    animation: 'wheelSpin 160s linear infinite',
    willChange: 'transform',
    WebkitMaskImage: 'radial-gradient(circle at 50% 50%, rgba(0,0,0,1) 98%, rgba(0,0,0,0) 100%)',
    maskImage: 'radial-gradient(circle at 50% 50%, rgba(0,0,0,1) 98%, rgba(0,0,0,0) 100%)',
    opacity: 1,
  },
  vignette: {
    position: 'fixed',
    inset: 0,
    zIndex: 0,
    pointerEvents: 'none',
    background: 'radial-gradient(120vmax 120vmax at 50% 45%, transparent 0 62%, rgba(0,0,0,0.10) 90%, rgba(0,0,0,0.18) 100%)',
  },

  /* Header */
  header: {
    position: 'sticky',
    top: 0,
    zIndex: 5,
    padding: 'max(10px, env(safe-area-inset-top)) 14px 10px',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    background: 'linear-gradient(180deg, rgba(0,0,0,0.42), rgba(0,0,0,0.18))',
    borderBottom: '1px solid rgba(218,165,32,0.18)',
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
  },
  backButton: {
    padding: '10px 12px',
    borderRadius: 12,
    border: '1px solid rgba(218,165,32,0.55)',
    background: 'linear-gradient(135deg, #DAA520, #FFD54F)',
    color: '#4B0082',
    fontWeight: 900,
    cursor: 'pointer',
    boxShadow: '0 10px 24px rgba(218,165,32,0.28), 0 4px 10px rgba(0,0,0,0.18)',
    textShadow: '0 1px 1px rgba(0,0,0,0.25)',
    whiteSpace: 'nowrap',
  },
  headerTitleWrap: { flex: 1, minWidth: 0 },
  headerTitle: {
    fontFamily: '"Cinzel", "Cormorant Garamond", serif',
    fontWeight: 800,
    letterSpacing: '0.02em',
    color: 'rgba(255, 234, 160, 0.92)',
    textShadow: '0 1px 0 rgba(0,0,0,0.55), 0 0 10px rgba(218,165,32,0.35)',
    fontSize: 'clamp(1.05rem, 1.2rem + 1.2vw, 1.7rem)',
    lineHeight: 1.05,
  },
  headerSub: {
    marginTop: 2,
    fontSize: 12,
    color: 'rgba(233, 224, 255, 0.9)',
    opacity: 0.9,
  },

  /* Layout */
  main: {
    position: 'relative',
    zIndex: 2,
    padding: '18px 14px max(24px, env(safe-area-inset-bottom))',
    display: 'flex',
    justifyContent: 'center',
  },

  /* Card */
  card: {
    width: '100%',
    maxWidth: 920,
    background: 'linear-gradient(180deg, rgba(255,255,255,0.86), rgba(255,255,255,0.78))',
    borderRadius: 18,
    padding: '22px 18px',
    boxShadow: '0 22px 60px rgba(0,0,0,0.22), 0 2px 8px rgba(218,165,32,0.10)',
    border: '1px solid rgba(218,165,32,0.28)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    color: '#2a0051',
  },

  badgeRow: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 10px',
    borderRadius: 999,
    background: 'linear-gradient(135deg, rgba(218,165,32,0.22), rgba(218,165,32,0.12))',
    border: '1px solid rgba(218,165,32,0.42)',
    color: '#4B0082',
    fontWeight: 900,
    fontSize: 12,
  },
  badgeAlt: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 10px',
    borderRadius: 999,
    background: 'rgba(75,0,130,0.08)',
    border: '1px solid rgba(75,0,130,0.18)',
    color: '#4B0082',
    fontWeight: 800,
    fontSize: 12,
  },
  badgeAlt2: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 10px',
    borderRadius: 999,
    background: 'rgba(0,0,0,0.06)',
    border: '1px solid rgba(0,0,0,0.10)',
    color: '#4B0082',
    fontWeight: 800,
    fontSize: 12,
  },

  lead: {
    margin: '10px 0 12px',
    lineHeight: 1.65,
    fontSize: 15,
    color: '#2a0051',
  },

  section: { marginTop: 12 },
  h2: {
    margin: '0 0 8px',
    color: '#4B0082',
    fontWeight: 900,
    fontSize: 16,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  h2Dot: {
    width: 24,
    height: 24,
    borderRadius: 999,
    display: 'grid',
    placeItems: 'center',
    background: 'linear-gradient(135deg, rgba(218,165,32,0.92), rgba(255,213,79,0.92))',
    color: '#2a0051',
    fontWeight: 900,
    fontSize: 12,
    boxShadow: '0 8px 18px rgba(218,165,32,0.28)',
    flexShrink: 0,
  },
  p: { margin: '0 0 8px', lineHeight: 1.65, fontSize: 14, color: '#2a0051' },

  list: {
    margin: '0 0 6px 18px',
    padding: 0,
    lineHeight: 1.6,
    color: '#2a0051',
  },

  divider: {
    height: 1,
    margin: '14px 0',
    background: 'linear-gradient(90deg, transparent, rgba(218,165,32,0.55), rgba(75,0,130,0.25), transparent)',
  },

  callout: {
    marginTop: 10,
    display: 'flex',
    gap: 10,
    alignItems: 'flex-start',
    padding: '12px 12px',
    borderRadius: 14,
    background: 'linear-gradient(180deg, rgba(75,0,130,0.06), rgba(218,165,32,0.06))',
    border: '1px solid rgba(75,0,130,0.16)',
  },
  calloutIcon: {
    width: 30,
    height: 30,
    borderRadius: 999,
    display: 'grid',
    placeItems: 'center',
    background: 'rgba(218,165,32,0.22)',
    border: '1px solid rgba(218,165,32,0.45)',
    color: '#4B0082',
    flexShrink: 0,
  },
  calloutTitle: { fontWeight: 900, color: '#4B0082', marginBottom: 2 },
  calloutText: { fontSize: 13, color: '#2a0051', lineHeight: 1.5 },

  footerNote: {
    marginTop: 16,
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    color: '#4B0082',
    opacity: 0.92,
    background: 'rgba(255,255,255,0.55)',
    border: '1px solid rgba(75,0,130,0.12)',
    borderRadius: 12,
    padding: '10px 12px',
  },
};
